/** Resolve image URL from food master row metadata */
export type FoodMasterRow = {
  code: string;
  name: string;
  metadata?: Record<string, unknown>;
};

export function masterImageUrl(row?: FoodMasterRow | null): string {
  if (!row?.metadata) return '';
  return String(row.metadata.imageUrl || row.metadata.logoUrl || '');
}

export function findFoodImageFromMasters(
  foodImages: FoodMasterRow[],
  foodCode: string,
  imageType: 'thumbnail' | 'gallery' = 'thumbnail',
): string {
  const match = foodImages.find((row) => {
    const meta = row.metadata || {};
    return String(meta.foodCode || '') === foodCode && String(meta.imageType || 'thumbnail') === imageType;
  });
  return masterImageUrl(match);
}

export function resolveFoodThumbnailFromMasters(
  opts: {
    foodCode?: string;
    categoryCode?: string;
    cuisineCode?: string;
    foodImages?: FoodMasterRow[];
    categories?: FoodMasterRow[];
    cuisines?: FoodMasterRow[];
  },
): string {
  const { foodCode, categoryCode, cuisineCode, foodImages = [], categories = [], cuisines = [] } = opts;
  if (foodCode) {
    const fromFood = findFoodImageFromMasters(foodImages, foodCode, 'thumbnail');
    if (fromFood) return fromFood;
  }
  if (categoryCode) {
    const cat = categories.find((c) => c.code === categoryCode);
    const url = masterImageUrl(cat);
    if (url) return url;
  }
  if (cuisineCode) {
    const cuisine = cuisines.find((c) => c.code === cuisineCode);
    return masterImageUrl(cuisine);
  }
  return '';
}

export function resolveRestaurantImagesFromMasters(
  cuisineCodes: string[],
  restaurantTypeCode: string,
  cuisines: FoodMasterRow[],
  restaurantTypes: FoodMasterRow[],
): { logo: string; coverImage: string } {
  const primaryCuisine = cuisines.find((c) => cuisineCodes.includes(c.code));
  const typeRow = restaurantTypes.find((t) => t.code === restaurantTypeCode);
  const cuisineImg = masterImageUrl(primaryCuisine);
  const typeImg = masterImageUrl(typeRow);
  return {
    logo: cuisineImg || typeImg,
    coverImage: typeImg || cuisineImg,
  };
}

export type OpeningHourRow = { day: string; openTime: string; closeTime: string; isClosed?: boolean };

export const WEEK_DAYS = [
  { value: 'mon', label: 'Mon' }, { value: 'tue', label: 'Tue' }, { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' }, { value: 'fri', label: 'Fri' }, { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

export function defaultOpeningHours(): OpeningHourRow[] {
  return WEEK_DAYS.map((d) => ({ day: d.value, openTime: '10:00', closeTime: '22:00', isClosed: false }));
}

export function openingHoursFromMasters(templates: FoodMasterRow[]): OpeningHourRow[] {
  const byDay: Record<string, OpeningHourRow> = {};
  for (const row of templates) {
    const meta = row.metadata || {};
    const day = String(meta.day || '');
    if (!day) continue;
    byDay[day] = {
      day,
      openTime: String(meta.openTime || '10:00'),
      closeTime: String(meta.closeTime || '22:00'),
      isClosed: Boolean(meta.isClosed),
    };
  }
  return WEEK_DAYS.map((d) => byDay[d.value] || { day: d.value, openTime: '10:00', closeTime: '22:00', isClosed: false });
}
